import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Database, Lock, Mail } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [retryMsg, setRetryMsg] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const isNetworkError = (err) =>
    !err.response && (err.code === "ERR_NETWORK" || err.message?.includes("Network Error") || err.message?.includes("ERR_CONNECTION_RESET"));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setRetryMsg("");

    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 1200;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        await login(email, password);
        navigate("/dashboard");
        return;
      } catch (err) {
        if (isNetworkError(err) && attempt < MAX_RETRIES) {
          setRetryMsg(`Bağlantı hatası — yeniden deneniyor (${attempt}/${MAX_RETRIES - 1})...`);
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
          continue;
        }
        // Auth error or final attempt
        setRetryMsg("");
        const msg = err.response?.data?.detail || (isNetworkError(err) ? "Sunucuya ulaşılamıyor. Lütfen biraz bekleyip tekrar deneyin." : "Giriş başarısız");
        toast.error(msg);
        break;
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm fade-in">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
            <Database size={18} className="text-white" strokeWidth={2} />
          </div>
          <div>
            <p className="text-base font-bold text-slate-900 leading-tight">
              Soru Bankası
            </p>
            <p className="text-xs text-slate-400">Yönetim Paneli</p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
          <div className="mb-6">
            <h1 className="text-xl font-bold text-slate-900">
              Admin Girişi
            </h1>
            <p className="text-sm text-slate-500 mt-1">Devam etmek için giriş yapın</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                E-Posta
              </Label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@qbank.com"
                  required
                  data-testid="email-input"
                  className="pl-9 h-9 text-sm border-slate-200"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium text-slate-600 uppercase tracking-wider">
                Şifre
              </Label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                  className="pl-9 h-9 text-sm border-slate-200"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-9 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium mt-2"
            >
              {loading ? (retryMsg || "Giriş yapılıyor...") : "Giriş Yap"}
            </Button>
            {retryMsg && (
              <p className="text-xs text-amber-600 text-center mt-1" data-testid="login-retry-msg">
                {retryMsg}
              </p>
            )}
          </form>

        </div>
      </div>
    </div>
  );
}
